import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { TrackedNav } from '@objectiv/tracker-react';
import { useBreakpoint } from '@insify/react-hooks';
import { COUNTRY } from '~globals';
import { BaseLink } from '~components/BaseLink';
import { ComponentLink } from '~components/ComponentLink';
import { useSiteConfig } from '~hooks/useSiteConfig';
import { LinkMenu } from '../LinkMenu';
import { MobileMenuToggle } from '../MobileMenuToggle';
import { CTA } from '../CTA';

import InsifyLogo from '../assets/insify-logo.svg';

import * as styles from './NavigationBar.module.css';

import type { ContentfulComponentLinkOrContentfulComponentLinkMenu } from '~components/types';
import type { HeaderCta } from '~models/contentful';

type NavigationBarProps = {
  headerCta?: HeaderCta;
};

export const NavigationBar: React.FC<NavigationBarProps> = ({ headerCta }) => {
  const { headerPinnedItems, headerDynamicItems, headerMyInsify } = useSiteConfig(COUNTRY);
  const isDesktop = useBreakpoint('(min-width: 1152px)');
  const [openMobileMenu, setOpenMobileMenu] = useState<boolean>(false);
  const bodyScrollPosition = useRef<number>(0);

  useEffect(() => {
    if (isDesktop || !openMobileMenu) {
      document.documentElement.style.overflowY = 'initial';
      document.documentElement.scrollTop = bodyScrollPosition.current;
      document.documentElement.style.scrollBehavior = '';
    } else {
      bodyScrollPosition.current = document.documentElement.scrollTop;
      document.documentElement.style.overflowY = 'hidden';
      document.documentElement.style.scrollBehavior = 'initial';
    }

    return () => {
      document.documentElement.style.overflowY = '';
      document.documentElement.style.scrollBehavior = '';
    };
  }, [isDesktop, openMobileMenu]);

  const pinnedItems = headerPinnedItems || [];
  const dynamicItems = headerDynamicItems || [];

  return (
    <div className={clsx(styles.navigationBar, openMobileMenu && styles.open)}>
      <BaseLink className={styles.homeLink} href='/' trackingId='home-link'>
        <InsifyLogo id='insify-header-logo' className={styles.insifyLogo} />
      </BaseLink>
      <TrackedNav
        className={clsx(
          styles.navigation,
          openMobileMenu && styles.open,
          openMobileMenu && !isDesktop && 'openMobileMenu',
        )}
        objectiv={{ id: 'navigation-bar' }}
      >
        {renderItems(pinnedItems, dynamicItems)}
        {headerMyInsify && (
          <ComponentLink className={styles.myInsifyLink} link={headerMyInsify}>
            {headerMyInsify.label}
          </ComponentLink>
        )}
      </TrackedNav>
      <div id='header-cta-button' className={styles.CTAAndMobileMenuToggle}>
        <div className={styles.CTAWrapper}>
          <CTA className={styles.CTA} headerCta={headerCta} />
        </div>
        <MobileMenuToggle
          className={styles.mobileMenuToggle}
          onClick={() => setOpenMobileMenu(!openMobileMenu)}
          isOpen={openMobileMenu}
        />
      </div>
    </div>
  );
};

const renderItems = (
  pinnedItems: ContentfulComponentLinkOrContentfulComponentLinkMenu[],
  dynamicItems: ContentfulComponentLinkOrContentfulComponentLinkMenu[],
) => {
  const allItems = [...pinnedItems, ...dynamicItems];

  if (!allItems.length) return null;

  return allItems.map((item, index) => {
    switch (item.__typename) {
      case 'ContentfulComponentLink':
        return (
          <ComponentLink key={item.label} className={styles.link} activeClassName={styles.active} link={item}>
            {item.label}
          </ComponentLink>
        );
      case 'ContentfulComponentLinkMenu':
        return (
          <LinkMenu
            key={item.label}
            className={clsx(styles.linkMenu, index >= pinnedItems.length && styles.dynamic)}
            linkMenu={item}
            order={index + 1}
          />
        );
      default:
        return null;
    }
  });
};
